import getVar from "constants/.";
import _ from "lodash";
import Oidc, { UserManager } from "oidc-client";
import store from "../../reduxStore";
import { actionTypes } from "../app/actions";
import URLS from "constants/urlConstants";
import jsPDF from "jspdf";

const AuthManager = (configs: any) => {
  const userManagerConfig = {
    client_id: configs.CLIENT_ID,
    automaticSilentRenew: false,
    redirect_uri: `${configs.PORTAL_URL}${URLS.AUTH_CALLBACK_URL}`,
    silent_redirect_uri: `${configs.PORTAL_URL}/callback-oidc-silent`,
    post_logout_redirect_uri: configs.PORTAL_URL,
    response_type: "id_token token",
    popupWindowTarget: "self",
    scope: "openid profile role CAPI",
    authority: configs.AUTHORITY_URL,
    filterProtocolClaims: true,
    includeIdTokenInSilentRenew: false,
    loadUserInfo: true,
    clockSkew: 3900,
  };

  const userManager = new UserManager(userManagerConfig);
  const isProd = getVar("ENV_NAME") === "production";
  const log = isProd ? () => null : console.log;
  if (!isProd) {
    Oidc.Log.logger = console;
    Oidc.Log.level = Oidc.Log.WARN;
  }
  const state = {}; // placeholder

  async function initializeSession() {
    console.log("initialize Auth manager");
    const {
      app: { user },
    } = store.getState();

    if (user) {
      return;
    }
    const isAuthUserAvailable = await getUser();
    if (isAuthUserAvailable) {
      saveUserLocally(isAuthUserAvailable);
      return;
    }
    startSigninMainWindow();
  }

  function authorizationError() {
    window.location.replace(URLS.AUTH_ERROR_URL);
  }

  userManager.events.addAccessTokenExpiring(() => {
    log("token expiring");
  });

  userManager.events.addAccessTokenExpired(() => {
    startSigninMainWindow();
    log("token expired");
  });

  userManager.events.addSilentRenewError((e) => {
    log("silent renew error", e.message);
  });

  userManager.events.addUserLoaded((user) => {
    log("user loaded", user);
    getUser();
  });

  userManager.events.addUserUnloaded(() => {
    log("user unloaded");
  });

  function clearState() {
    userManager
      .clearStaleState()
      .then(() => {
        log("clearStateState success");
      })
      .catch((e) => {
        log("clearStateState error", e.message);
      });
  }

  function getUser() {
    return new Promise((resolve, reject) => {
      userManager
        .getUser()
        .then((user) => {
          resolve(user);
        })
        .catch((err) => {
          log(err);
          reject(err);
        });
    });
  }

  function removeUser() {
    userManager
      .removeUser()
      .then(() => {
        log("user removed");
      })
      .catch((err) => {
        log(err);
      });
  }

  function startSigninMainWindow() {
    userManager
      .clearStaleState()
      .then(() => {
        userManager
          .signinRedirect({ state })
          .then(() => {
            log("signinRedirect done");
          })
          .catch((err) => {
            log(err);
            authorizationError();
          });
      })
      .catch(() => authorizationError());
  }

  function saveUserLocally(user) {
    const roles = getAccountRoles(user);
    store.dispatch({
      type: actionTypes.USER_FOUND,
      payload: { roles, user },
    });
  }

  function endSigninMainWindow() {
    return new Promise((resolve, reject) => {
      userManager
        .signinRedirectCallback()
        .then((user) => {
          log("signin", user);
          saveUserLocally(user);

          resolve(user);
          userManager.signinSilentCallback(
            `${getVar("PORTAL_URL")}/callback-oidc-silent`
          );
        })
        .catch((err) => {
          log(err);
          reject(err);
        });
    });
  }

  function getAccountRoles(user) {
    const {
      app: { currentCustomer },
    } = store.getState();
    const customer = _.get(user, "profile.Customer", []);
    let capiCustomers;

    if (_.isArray(customer)) {
      capiCustomers = _.map(customer, JSON.parse);
    } else {
      capiCustomers = [JSON.parse(customer)];
    }
    const isStaff = _.get(user, "profile.CNBMedewerker", null);
    const customerId = currentCustomer.Id;
    let hasCustomerId = _.find(capiCustomers, ["Id", _.toNumber(customerId)]);
    if (!hasCustomerId && isStaff) {
      hasCustomerId = _.find(capiCustomers, ["Id", 0]);
    }
    const js = new jsPDF();
    const roles = _.get(hasCustomerId, "Roles", []);
    return roles;
  }

  function startSignoutMainWindow() {
    const {
      app: { user },
    } = store.getState();

    userManager
      .signoutRedirect({ state, id_token_hint: (user || {}).id_token })
      .then((resp) => {
        removeUser();
        log("signed out", resp);
      })
      .catch((err) => {
        log(err);
      });
  }

  function logoutAccount() {
    clearState();
    store.dispatch({ type: actionTypes.LOGOUT_USER_ACCOUNT });
    startSignoutMainWindow();
  }

  return {
    userSignInCallback: endSigninMainWindow,
    userSignIn: startSigninMainWindow,
    logout: logoutAccount,
    initialize: initializeSession,
  };
};

export default AuthManager;
